package ai.jessie.web.onboarding

import ai.jessie.shared.onboarding.CompleteOnboardingRequest
import ai.jessie.shared.onboarding.EntryResponse
import ai.jessie.shared.onboarding.OnboardingContextResponse
import ai.jessie.shared.onboarding.OnboardingErrorResponse
import ai.jessie.shared.onboarding.OrganisationSelectionResponse
import ai.jessie.shared.onboarding.OrganisationStartResponse
import ai.jessie.shared.onboarding.SelectOrganisationRequest
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import kotlin.js.Promise
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import org.w3c.fetch.ERROR
import org.w3c.fetch.Headers
import org.w3c.fetch.NO_STORE
import org.w3c.fetch.RequestCache
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.fetch.RequestRedirect
import org.w3c.fetch.SAME_ORIGIN

interface OnboardingPort {
    suspend fun context(): OnboardingContextResponse

    suspend fun complete(request: CompleteOnboardingRequest): EntryResponse

    suspend fun entry(): EntryResponse

    suspend fun select(request: SelectOrganisationRequest): EntryResponse

    suspend fun start(organisationId: String): OrganisationStartResponse

    fun startedRole(organisationId: String): String? = null
}

// UC-004 selector proposal; separate so onboarding-only ports need not list organisations.
fun interface OrganisationSelectionPort {
    suspend fun organisations(): OrganisationSelectionResponse
}

val LocalOnboardingPort =
    staticCompositionLocalOf<OnboardingPort> {
        error("Onboarding port must be supplied by the application")
    }

val LocalOrganisationSelectionPort =
    staticCompositionLocalOf<OrganisationSelectionPort> {
        error("Organisation selection port must be supplied by the application")
    }

class OnboardingRequestFailure(
    val status: Int,
    val code: String,
    val fields: List<String> = emptyList(),
) : Exception("Onboarding request failed")

class BrowserOnboardingPort : OnboardingPort, OrganisationSelectionPort {
    private val json = Json { ignoreUnknownKeys = true }
    private var startedRole by mutableStateOf(readStartedRole())

    override fun startedRole(organisationId: String): String? =
        startedRole?.takeIf { it.first.equals(organisationId, ignoreCase = true) }?.second

    override suspend fun context() =
        json.decodeFromString<OnboardingContextResponse>(request("/onboarding"))

    override suspend fun complete(request: CompleteOnboardingRequest) =
        json.decodeFromString<EntryResponse>(
            request("/onboarding/complete", json.encodeToString(request))
        )

    override suspend fun entry(): EntryResponse {
        rememberStartedRole(null)
        return json.decodeFromString(request("/entry", ""))
    }

    override suspend fun organisations() =
        json.decodeFromString<OrganisationSelectionResponse>(request("/organisations"))

    override suspend fun select(request: SelectOrganisationRequest): EntryResponse {
        rememberStartedRole(null)
        return json.decodeFromString(request("/entry/select", json.encodeToString(request)))
    }

    override suspend fun start(organisationId: String): OrganisationStartResponse {
        require(validOrganisationId(organisationId))
        rememberStartedRole(null)
        return json
            .decodeFromString<OrganisationStartResponse>(
                request("/organisations/$organisationId/start")
            )
            .also {
                require(it.organisationId.equals(organisationId, ignoreCase = true))
                rememberStartedRole(it.organisationId to it.role)
            }
    }

    private fun rememberStartedRole(value: Pair<String, String>?) {
        startedRole = value
        if (value == null) window.sessionStorage.removeItem(STARTED_ROLE_KEY)
        else window.sessionStorage.setItem(STARTED_ROLE_KEY, "${value.first}|${value.second}")
    }

    private suspend fun request(path: String, body: String? = null) = apiRequest(json, path, body)
}

private fun readStartedRole(): Pair<String, String>? {
    val parts = window.sessionStorage.getItem(STARTED_ROLE_KEY)?.split('|') ?: return null
    return if (parts.size == 2 && validOrganisationId(parts[0])) parts[0] to parts[1] else null
}

private const val STARTED_ROLE_KEY = "jessie.organisationStartRole"

// Shared by the application's browser ports: same-origin credentials and one error decoding.
// `success` is the single status that carries a body to decode; UC-013 creation answers 201.
internal suspend fun apiRequest(
    json: Json,
    path: String,
    body: String? = null,
    success: Int = HTTP_OK,
    decodeFailure: ((Int, String) -> Exception)? = null,
): String {
    val response =
        window
            .fetch(
                "/api/v1$path",
                RequestInit(
                    method = if (body == null) "GET" else "POST",
                    body = body,
                    headers =
                        Headers().also {
                            if (body != null) it.set("Content-Type", "application/json")
                        },
                    credentials = RequestCredentials.SAME_ORIGIN,
                    cache = RequestCache.NO_STORE,
                    redirect = RequestRedirect.ERROR,
                ),
            )
            .awaitNetworkResponse()
    val text = response.text().awaitNetworkResponse()
    if (response.status.toInt() != success) {
        decodeFailure?.let { throw it(response.status.toInt(), text) }
        val error = json.decodeProblem<OnboardingErrorResponse?>(text, null)
        throw OnboardingRequestFailure(
            response.status.toInt(),
            error?.code.orEmpty(),
            error?.fields.orEmpty(),
        )
    }
    return text
}

// A failure body as the endpoint's problem type, or [empty] when it is not one (for example a
// proxy's HTML error page).
internal inline fun <reified P> Json.decodeProblem(text: String, empty: P): P =
    try {
        decodeFromString<P>(text)
    } catch (_: IllegalArgumentException) {
        empty
    }

internal suspend fun <T> Promise<T>.awaitNetworkResponse(): T =
    catch<T> { throw OnboardingRequestFailure(0, "NETWORK_UNAVAILABLE") }.await()

fun validOrganisationId(value: String): Boolean =
    value.matches(
        Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")
    ) && value != "00000000-0000-0000-0000-000000000000"

private const val HTTP_OK = 200
